import type { createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import {
  AddMatterContactConnectionForm,
  type AddMatterContactConnectionFormProps,
} from "@/components/features/matter/layout/AddOrEditMatterContactConnectionSlideover/AddMatterContactConnectionForm";
import {
  EditMatterContactConnectionForm,
  type EditMatterContactConnectionFormHandle,
  type EditMatterContactConnectionFormProps,
} from "@/components/features/matter/layout/AddOrEditMatterContactConnectionSlideover/EditMatterContactConnectionForm";
import { Button, Label, LoadingSpinner, Slideover } from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { useUser } from "@/components/UserProvider";
import { type MATTER_CONTACT_CONNECTION_LABEL_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type Contact,
  type MatterContactConnection,
  type MatterSubItemType,
} from "@/lib/types";
import { getRemoveMatterContactConnectionModalCopy } from "@/lib/utils/matterContactConnectionHelpers";
import type { MatterContactLabelPair } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { Suspense, useEffect, useRef, useState } from "react";
import type { z } from "zod";

interface AddOrEditMatterContactConnectionSlideoverProps {
  disableFormSubmit?: boolean;
  disableTime?: boolean;
  disableDelete?: boolean;
  isOpen: boolean;
  labelOptions: (typeof MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)[number][];
  disabledOptions?: (typeof MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)[number][];

  matterId: string;
  setIsOpen: (open: boolean) => void;
  title?: string;
  description?: string;
  subItem?: MatterSubItemType;
  subItemId?: string;
  overrideCreateFn?: AddMatterContactConnectionFormProps["overrideCreateFn"];
  overrideEditFn?: EditMatterContactConnectionFormProps["overrideEditFn"];
  overrideDeleteFn?: EditMatterContactConnectionFormProps["overrideDeleteFn"];
  matterContactConnection?: MatterContactConnection;
  /** When set, the edit-modal preview column skips `retrieveContact`. */
  previewContact?: Contact;
  defaultCreateContactValues?: Partial<z.infer<typeof createContactFormSchema>>;
  defaultContactSearchObject?: { [key: string]: string[] };
  excludeContactIds?: string[];
  reservedContactLabelPairs?: MatterContactLabelPair[];
  autoOpenContactSelection?: boolean;
  selectContactOnly?: boolean;
  /** Edit flow: centered split modal (preview + form). */
  editPresentation?: "slideover" | "modal";
  /** Create flow (add connection). Default `modal`; pass `slideover` for the legacy sheet. */
  createPresentation?: "slideover" | "modal";
}

export const AddOrEditMatterContactConnectionSlideover = ({
  matterContactConnection,
  previewContact,
  disableFormSubmit,
  disableDelete,
  disableTime,
  isOpen,
  labelOptions,
  disabledOptions,
  matterId,
  setIsOpen,
  title,
  description,
  defaultCreateContactValues,
  defaultContactSearchObject,
  excludeContactIds,
  reservedContactLabelPairs,
  autoOpenContactSelection,
  selectContactOnly,
  editPresentation = "slideover",
  createPresentation = "modal",
  ...props
}: AddOrEditMatterContactConnectionSlideoverProps) => {
  const { hasActionPermission } = useUser();
  const hasDeleteMatterContactConnectionPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasCreateContactPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.create,
  );
  const [isDirty, setIsDirty] = useState(false);
  const [inlineEmbeddedCreate, setInlineEmbeddedCreate] = useState({
    active: false,
    dirty: false,
    loading: false,
  });
  const [inlineEmbeddedCreateOpen, setInlineEmbeddedCreateOpen] =
    useState(false);
  const [isSavingEditModal, setIsSavingEditModal] = useState(false);
  const editFormRef = useRef<EditMatterContactConnectionFormHandle>(null);

  useEffect(() => {
    if (!isOpen) {
      setInlineEmbeddedCreate({
        active: false,
        dirty: false,
        loading: false,
      });
      setInlineEmbeddedCreateOpen(false);
      setIsSavingEditModal(false);
    }
  }, [isOpen]);

  const activeConnectionId = matterContactConnection?.id;
  const activeContactId = matterContactConnection?.contactId;

  const showEditModal =
    editPresentation === "modal" &&
    Boolean(activeConnectionId) &&
    Boolean(activeContactId) &&
    Boolean(matterContactConnection);
  const isCreateFlow = !activeConnectionId;
  const addFormShell: "slideover" | "modal" =
    isCreateFlow && (selectContactOnly || createPresentation === "modal")
      ? "modal"
      : "slideover";

  const addMatterContactConnectionForm = (
    <AddMatterContactConnectionForm
      setIsDirty={setIsDirty}
      labelOptions={labelOptions}
      disabledOptions={disabledOptions}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      matterId={matterId}
      disableFormSubmit={disableFormSubmit}
      disableTime={disableTime}
      defaultCreateContactValues={defaultCreateContactValues}
      defaultContactSearchObject={defaultContactSearchObject}
      excludeContactIds={excludeContactIds}
      reservedContactLabelPairs={reservedContactLabelPairs}
      autoOpenContactSelection={
        selectContactOnly ? false : autoOpenContactSelection
      }
      selectContactOnly={selectContactOnly}
      formShell={addFormShell}
      onInlineEmbeddedCreateStateChange={setInlineEmbeddedCreate}
      inlineEmbeddedCreateOpen={inlineEmbeddedCreateOpen}
      onInlineEmbeddedCreateOpenChange={setInlineEmbeddedCreateOpen}
      inlineEmbeddedCreateActive={
        inlineEmbeddedCreate.active || inlineEmbeddedCreateOpen
      }
      {...props}
    />
  );

  if (selectContactOnly && !activeConnectionId) {
    const embeddedCreateUi =
      inlineEmbeddedCreate.active || inlineEmbeddedCreateOpen;
    return (
      <AppModal
        open={isOpen}
        onOpenChange={setIsOpen}
        title={
          embeddedCreateUi ? "Create contact" : (title ?? "Select contact")
        }
        description={embeddedCreateUi ? "Create a new contact" : description}
        size={embeddedCreateUi ? "3xl" : "2xl"}
        className="h-[min(80vh,780px)] w-[calc(100vw-2rem)]"
        isDirty={inlineEmbeddedCreate.dirty || isDirty}
        loading={inlineEmbeddedCreate.loading}
        bodyClassName="min-h-0 px-2 pt-3 pb-2"
        bodyContained
        footer={
          !embeddedCreateUi && hasCreateContactPermission ? (
            <div className="flex w-full">
              <Button
                type="button"
                variant="outline"
                className="w-full justify-center"
                onClick={() => {
                  setInlineEmbeddedCreateOpen(true);
                }}
              >
                Create new contact
              </Button>
            </div>
          ) : undefined
        }
      >
        {addMatterContactConnectionForm}
      </AppModal>
    );
  }

  if (createPresentation === "modal" && !activeConnectionId) {
    return (
      <AppModal
        open={isOpen}
        onOpenChange={setIsOpen}
        title={title ?? "Create contact connection"}
        description={description}
        size="3xl"
        className="h-[min(80vh,780px)] w-[calc(100vw-2rem)]"
        isDirty={isDirty}
        bodyContained
      >
        {addMatterContactConnectionForm}
      </AppModal>
    );
  }

  if (showEditModal) {
    const editContactId = activeContactId;
    const editConnection = matterContactConnection;
    if (!editContactId || !editConnection) {
      return null;
    }
    const removeMatterContactModalCopy =
      getRemoveMatterContactConnectionModalCopy(editConnection.label);
    return (
      <AppModal
        open={isOpen}
        onOpenChange={setIsOpen}
        title={title ?? `Edit ${editConnection.label} information`}
        description={description}
        size="3xl"
        className="h-[min(80vh,780px)] w-[calc(100vw-2rem)]"
        isDirty={isDirty}
        loading={isSavingEditModal}
        bodyContained
        preview={
          <MatterContactConnectionModalPreview
            contactId={editContactId}
            previewContact={previewContact}
            matterContactConnection={editConnection}
            matterId={matterId}
          />
        }
        footer={
          disableFormSubmit ? undefined : (
            <div className="flex w-full flex-wrap items-center gap-2">
              {hasDeleteMatterContactConnectionPermission && !disableDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={isSavingEditModal}
                  onClick={() => {
                    editFormRef.current?.requestDelete();
                  }}
                >
                  {removeMatterContactModalCopy.deleteButtonText}
                </Button>
              ) : null}
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                <FormCancelButton setOpen={setIsOpen} isDirty={isDirty} />
                <Button
                  type="button"
                  disabled={isSavingEditModal || !isDirty}
                  onClick={() => {
                    void (async () => {
                      if (!editFormRef.current) {
                        return;
                      }
                      setIsSavingEditModal(true);
                      try {
                        const ok = await editFormRef.current.submitCombined();
                        if (ok) {
                          setIsOpen(false);
                        }
                      } finally {
                        setIsSavingEditModal(false);
                      }
                    })();
                  }}
                >
                  {isSavingEditModal ? <LoadingSpinner /> : "Save changes"}
                </Button>
              </div>
            </div>
          )
        }
      >
        <Suspense
          fallback={
            <div className="flex flex-col gap-y-4 py-4">
              <Label>Connection</Label>
              <LoadingSpinner />
            </div>
          }
        >
          <EditMatterContactConnectionForm
            key={editConnection.id}
            ref={editFormRef}
            setIsDirty={setIsDirty}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            matterId={matterId}
            disableFormSubmit={disableFormSubmit}
            disableDelete={disableDelete}
            disableTime={disableTime}
            matterContactConnectionContactId={editContactId}
            matterContactConnection={editConnection}
            labelOptions={labelOptions}
            contactEditorMode="hidden"
            hideInternalFooter
            closeOnSuccessfulSubmit={false}
            {...props}
          />
        </Suspense>
      </AppModal>
    );
  }

  return (
    <Slideover
      title={title ?? "Create contact connection"}
      description={description}
      open={isOpen}
      setOpen={setIsOpen}
      isDirty={isDirty}
    >
      {activeConnectionId && activeContactId ? (
        <Suspense
          fallback={
            <div className="mt-4 flex flex-col gap-y-4 p-6">
              <Label>Connection</Label>
              <LoadingSpinner />
            </div>
          }
        >
          <EditMatterContactConnectionForm
            setIsDirty={setIsDirty}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            matterId={matterId}
            disableFormSubmit={disableFormSubmit}
            disableDelete={disableDelete}
            disableTime={disableTime}
            matterContactConnectionContactId={activeContactId}
            matterContactConnection={matterContactConnection}
            labelOptions={labelOptions}
            {...props}
          />
        </Suspense>
      ) : (
        addMatterContactConnectionForm
      )}
    </Slideover>
  );
};
